import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Thermometer, Fan } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const HVACRepair = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/hvac-repair"]?.title} description={seoData["services/hvac-repair"]?.description} path="/services/hvac-repair" keywords={seoData["services/hvac-repair"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Chillers, Condensers & Cooling Towers"
        description="HVAC, chiller, and cooling tower repair and coating contractor in California. Tubesheet coating, condenser repair, cooling tower media fill replacement, and heat exchanger restoration. Extend equipment life and improve efficiency."
        backgroundKey="hvac-repair"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Condenser and evaporator tube sheet corrosion and pitting',
              'Cooling tower basin corrosion and structural deterioration',
              'Heat exchanger tube-to-tubesheet joint leaks',
              'Chiller barrel corrosion and refrigerant leaks',
              'Cooling tower fill media degradation and scaling',
              'Circulating water pipe corrosion and fouling'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-sky-50 rounded-lg">
                <Thermometer className="text-sky-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Fan className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Tube Sheet Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Epoxy coating systems specifically formulated for condenser and evaporator tube sheets. Prevent galvanic corrosion between copper tubes and steel tube sheets, seal tube-to-tubesheet joints, and extend equipment life without retube.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Fan className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Cooling Tower Restoration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Complete cooling tower rehabilitation including basin coating, structural concrete repair, fill media replacement, and distribution deck restoration. Improve thermal performance and extend tower service life.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Fan className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Chiller Barrel Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Internal chiller barrel coating and repair to address corrosion, erosion, and refrigerant-side protection. Restore chiller efficiency and prevent costly barrel replacement.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Fan className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Heat Exchanger Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Shell-side and tube-side coating for heat exchangers. Inlet waterbox lining, channel head coating, and tube sheet protection to prevent corrosion and extend exchanger life between maintenance cycles.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Extend chiller and cooling tower life 10-15 years',
                  'Avoid costly equipment replacement with targeted repair',
                  'Improve heat transfer efficiency with restored surfaces',
                  'Minimize HVAC downtime with scheduled repair windows',
                  'Certified for work on all major chiller and cooling tower brands',
                  'On-site repair — no equipment removal or transport required',
                  'Full QA/QC documentation for maintenance records',
                  'Emergency repair available for critical cooling system failures'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1321 (Ceramic S-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Erosion-resistant repair for water boxes</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5831 (ST-Barrier)</p>
                  <p className="text-xs text-slate-500 mt-1">Surface-tolerant tube sheet coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">General tube sheet and barrel repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4111 (Magma-Quartz)</p>
                  <p className="text-xs text-slate-500 mt-1">Cooling tower basin concrete repair</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">HVAC Equipment Failing? We Restore It.</h2>
          <p className="text-blue-100 mb-8">Contact our HVAC coating specialists for chiller, condenser, or cooling tower assessment and repair.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HVACRepair;
